<script setup>
import { ref } from "vue";
import { isTouchDevice } from "common/utils/browser";
import Stars from "common/vue/components/Stars.vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { track } from "jiffy-analytics";
import PdpMadeReview from "./PdpMadeReview.vue";

const props = defineProps({
  rating: {
    type: Number,
    required: true
  },
  reviews: {
    type: Array,
    required: true
  },
  reviewsCount: {
    type: Number,
    required: true
  }
});

let timerId = null;
const isTooltipOpen = ref(false);

const closeTooltip = () => {
  isTooltipOpen.value = false;
  clearTimeout(timerId);
};
const openTooltip = () => {
  if (isTouchDevice()) return;

  timerId = setTimeout(() => {
    isTooltipOpen.value = true;
    trackReviewEvent("made_pdp_review_opened");
  }, 500);
};
const toggleTooltip = () => {
  isTooltipOpen.value = !isTooltipOpen.value;
};

const handleReviewLinkClick = () => {
  toggleTooltip();
  trackReviewEvent("made_see_all_reviews_clicked");
};

const trackReviewEvent = event => {
  track(event, {
    source: "Custom Apparel Reviews"
  });
};
</script>

<template>
  <div v-click-outside="closeTooltip" class="pdp-made-reviews" @mouseleave="closeTooltip">
    <div class="pdp-made-reviews__widget">
      <a class="pdp-made-reviews__rating" href="#reviews-summary">
        <Stars class="pdp-made-reviews__stars" :stars="rating" size="S"></Stars>
        <span class="pdp-made-reviews__count">({{ reviewsCount }})</span>
      </a>

      <button
        class="pdp-made-reviews__tooltip-trigger"
        type="button"
        @click="toggleTooltip"
        @mouseenter="openTooltip"
      >
        <span class="pdp-made-reviews__tooltip-trigger-text">Custom Apparel Reviews</span>
        <UseSvg
          class="pdp-made-reviews__tooltip-trigger-icon"
          :class="{ 'pdp-made-reviews__tooltip-trigger-icon--flip': isTooltipOpen }"
          icon="chevron-down"
        />
      </button>
    </div>

    <div class="pdp-made-reviews__tooltip" :class="{ hidden: !isTooltipOpen }">
      <div class="pdp-made-reviews__tooltip-inner">
        <header class="pdp-made-reviews__tooltip-header">
          <h4 class="pdp-made-reviews__tooltip-header-title">Latest Reviews</h4>
          <a
            class="pdp-made-reviews__tooltip-header-link"
            href="#reviews-summary"
            @click="handleReviewLinkClick"
          >
            See all customer reviews
          </a>
        </header>

        <div class="pdp-made-reviews__tooltip-body">
          <div class="pdp-made-reviews__wrapper">
            <PdpMadeReview
              v-for="review in props.reviews"
              :key="review"
              :review="review"
              :toggle-tooltip="toggleTooltip"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/button/mixin.scss";

.pdp-made-reviews {
  display: inline-block;
  position: relative;

  &__widget {
    display: flex;
    gap: 12px;
  }

  &__rating {
    display: flex;
    align-items: center;
    gap: 4px;
    text-decoration: none;
  }

  &__count {
    color: $black;
    font-size: 15px;
  }

  &__tooltip-trigger {
    @include clear-button;
  }

  &__tooltip-trigger-text {
    color: $blue;
    font-size: 15px;
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  &__tooltip-trigger-icon {
    width: 13px;
    height: 13px;
    margin-left: 6px;
    color: $silver-chalice;
    vertical-align: -3px;

    &--flip {
      transform: rotate(180deg);
    }
  }

  &__tooltip {
    position: absolute;
    top: 100%;
    left: 0;
    max-width: calc(100vw - 10px);
    margin-left: -3px;
    padding-top: 8px;
    z-index: 2;

    @include with-tablet {
      margin-left: 7px;
    }
  }

  &__tooltip-inner {
    border: 1px solid $blue;
    border-radius: 8px;
    background-color: $white;
    box-shadow: 0px 10px 20px rgba($black, 0.25);
    overflow: hidden;
  }

  &__tooltip-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 40px;
    padding: 0 14px 0 10px;
    border-radius: 6px 6px 0 0;
    background-color: $jiffy-sample-cta-blue;
    color: $white;
    font-size: 14px;
  }

  &__tooltip-header-title {
    font-size: inherit;
  }

  &__tooltip-header-link {
    color: inherit;
    font-size: 14px;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  &__tooltip-body {
    max-height: 310px;
    padding: 10px 8px;
    border-radius: 0 0 6px 6px;
    overflow: auto;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}
</style>
